/**
 * Task Event Helpers
 *
 * Shared helper functions for task timeline event display.
 * Extracted from TaskProcessingFlow.tsx for reuse across TaskEventsList and other consumers.
 */

import React from 'react';
import {
  Check,
  Clock,
  Ship,
  Building2,
  Package,
  Truck,
  User,
  Camera,
  MapPin,
  Monitor,
} from 'lucide-react';
import {
  TaskTimelineEvent,
  TimeRecordSubtype,
} from '../../../types/timeline';

// ==================== Phase Types ====================

export type TaskPhase = 'preparation' | 'execution' | 'return' | 'completion';

export interface TaskPhaseData {
  name: string;
  name_cn: string;
  events: TaskTimelineEvent[];
  completed: number;
  total: number;
  status: 'completed' | 'in_progress' | 'pending';
}

// ==================== Status Labels ====================

const STATUS_LABELS: Record<string, string> = {
  'on-vessel': 'On Vessel',
  'released': 'Released',
  'on-hold': 'On Hold',
  'unassigned': 'Unassigned',
  'assigned': 'Assigned',
  'picking': 'Picking',
  'delivering': 'Delivering',
  'loading': 'Loading',
  'on-stop': 'On Stop',
  'empty': 'Empty',
  'loaded': 'Loaded',
  'returning': 'Returning',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
  'paused': 'Paused',
  'yard-stored': 'Yard Stored',
  'pcc-hold': 'PCC Hold',
  'uncompleted': 'Uncompleted',
};

// Map each task status to the phase it belongs to
const STATUS_TO_PHASE: Record<string, TaskPhase> = {
  'on-vessel': 'preparation',
  'released': 'preparation',
  'on-hold': 'preparation',
  'unassigned': 'preparation',
  'assigned': 'preparation',
  'picking': 'execution',
  'delivering': 'execution',
  'loading': 'execution',
  'on-stop': 'execution',
  'empty': 'execution',
  'loaded': 'execution',
  'paused': 'execution',
  'yard-stored': 'execution',
  'pcc-hold': 'execution',
  'returning': 'return',
  'completed': 'completion',
  'cancelled': 'completion',
  'uncompleted': 'completion',
};

// Ordered phases for comparison
const PHASE_ORDER: TaskPhase[] = ['preparation', 'execution', 'return', 'completion'];

// ==================== Icon Helpers ====================

export const getEventIcon = (event: TaskTimelineEvent): React.ReactElement => {
  const iconClass = 'w-3.5 h-3.5';

  if (event.event_type === 'status_change') {
    const status = event.status_to || '';
    if (['picking', 'delivering', 'returning'].includes(status)) {
      return React.createElement(Truck, { className: iconClass });
    }
    if (['on-vessel', 'released'].includes(status)) {
      return React.createElement(Ship, { className: iconClass });
    }
    if (['loading', 'on-stop'].includes(status)) {
      return React.createElement(Building2, { className: iconClass });
    }
    if (['empty', 'loaded'].includes(status)) {
      return React.createElement(Package, { className: iconClass });
    }
    if (status === 'completed') {
      return React.createElement(Check, { className: iconClass });
    }
    return React.createElement(Clock, { className: iconClass });
  }

  if (event.event_type === 'system_action') {
    if (event.event_subtype === 'task_created') {
      return React.createElement(Package, { className: iconClass });
    }
    return React.createElement(Monitor, { className: iconClass });
  }

  if (event.event_type === 'dispatcher_action') {
    if (event.event_subtype === 'port_appointment_made') {
      return React.createElement(Ship, { className: iconClass });
    }
    if (event.event_subtype === 'warehouse_appointment_made') {
      return React.createElement(Building2, { className: iconClass });
    }
    return React.createElement(User, { className: iconClass });
  }

  if (event.event_type === 'photo_upload') {
    return React.createElement(Camera, { className: iconClass });
  }

  if (event.event_type === 'location_update') {
    return React.createElement(MapPin, { className: iconClass });
  }

  // Time record subtypes
  const subtype = event.event_subtype as TimeRecordSubtype | undefined;
  switch (subtype) {
    case 'enter_terminal':
    case 'exit_terminal':
    case 'enter_terminal_2':
    case 'exit_terminal_2':
      return React.createElement(Ship, { className: iconClass });
    case 'arrive_warehouse':
    case 'on_dock':
    case 'exit_warehouse':
      return React.createElement(Building2, { className: iconClass });
    case 'empty':
    case 'loaded':
      return React.createElement(Package, { className: iconClass });
    default:
      return React.createElement(Clock, { className: iconClass });
  }
};

// ==================== Title/Label Helpers ====================

export const getEventTitle = (event: TaskTimelineEvent): string => {
  if (event.event_type === 'status_change') {
    const toStatus = event.status_to || '';
    return STATUS_LABELS[toStatus] || toStatus;
  }

  if (event.event_type === 'photo_upload') {
    return `Photo: ${event.photo_category || 'Document'}`;
  }

  if (event.event_type === 'system_action') {
    const systemSubtypes: Record<string, string> = {
      task_created: 'Task Created',
      auto_create_empty_in: 'Auto-created (Empty In)',
      auto_create_load_in: 'Auto-created (Load In)',
    };
    return systemSubtypes[event.event_subtype || ''] || 'System Action';
  }

  if (event.event_type === 'dispatcher_action') {
    const dispatcherSubtypes: Record<string, string> = {
      port_appointment_made: 'Port Appointment Set',
      warehouse_appointment_made: 'Warehouse Appointment Set',
      assign_driver: 'Driver Assigned',
      unassign_driver: 'Driver Unassigned',
    };
    return dispatcherSubtypes[event.event_subtype || ''] || 'Dispatcher Action';
  }

  const subtypeLabels: Record<string, string> = {
    enter_terminal: 'Enter Terminal',
    exit_terminal: 'Exit Terminal',
    enter_terminal_2: 'Enter Terminal (Return)',
    exit_terminal_2: 'Exit Terminal (Return)',
    arrive_warehouse: 'Arrive Warehouse',
    on_dock: 'On Dock',
    empty: 'Container Emptied',
    loaded: 'Container Loaded',
    exit_warehouse: 'Exit Warehouse',
    arrive_stop: 'Arrive Stop',
    leave_stop: 'Leave Stop',
    returning: 'Returning',
    returned: 'Returned',
  };

  return subtypeLabels[event.event_subtype || ''] || event.event_type;
};

export const getTriggeredByLabel = (event: TaskTimelineEvent): string => {
  const type = event.triggered_by;
  switch (type) {
    case 'driver':
      return event.triggered_by_name || 'Driver';
    case 'dispatcher':
      return event.triggered_by_name || 'Dispatcher';
    case 'system':
      return 'System';
    default:
      return event.triggered_by_name || 'System';
  }
};

export const getTriggeredByIcon = (event: TaskTimelineEvent): React.ReactElement => {
  const iconClass = 'w-3 h-3';
  const type = event.triggered_by;

  if (type === 'driver') {
    return React.createElement(Truck, { className: iconClass });
  }
  if (type === 'dispatcher') {
    return React.createElement(User, { className: iconClass });
  }
  return React.createElement(Monitor, { className: iconClass });
};

// ==================== Time Formatting ====================

export const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ==================== Event Categorization ====================

/**
 * Categorize timeline events into phases and infer phase status
 * from the task's current status.
 *
 * @param events - All timeline events for this task
 * @param currentStatus - The task's current status (e.g. 'delivering').
 *   When provided, phases are marked completed/in_progress/pending
 *   based on where currentStatus falls in the phase order.
 */
export const categorizeEvents = (
  events: TaskTimelineEvent[],
  currentStatus?: string,
): Record<TaskPhase, TaskPhaseData> => {
  const phases: Record<TaskPhase, TaskPhaseData> = {
    preparation: {
      name: 'Preparation',
      name_cn: '准备阶段',
      events: [],
      completed: 0,
      total: 0,
      status: 'pending',
    },
    execution: {
      name: 'Execution',
      name_cn: '执行阶段',
      events: [],
      completed: 0,
      total: 0,
      status: 'pending',
    },
    return: {
      name: 'Return',
      name_cn: '返回阶段',
      events: [],
      completed: 0,
      total: 0,
      status: 'pending',
    },
    completion: {
      name: 'Completion',
      name_cn: '完成阶段',
      events: [],
      completed: 0,
      total: 0,
      status: 'pending',
    },
  };

  // Categorize each event into a phase
  events.forEach((event) => {
    let phase: TaskPhase = 'execution';

    if (event.event_type === 'status_change') {
      const toStatus = event.status_to || '';
      phase = STATUS_TO_PHASE[toStatus] || 'execution';
    } else if (event.event_type === 'system_action') {
      const subtype = event.event_subtype || '';
      if (['task_created', 'auto_create_empty_in', 'auto_create_load_in'].includes(subtype)) {
        phase = 'preparation';
      }
    } else if (event.event_type === 'dispatcher_action') {
      const subtype = event.event_subtype || '';
      if (['port_appointment_made', 'warehouse_appointment_made', 'assign_driver', 'unassign_driver'].includes(subtype)) {
        phase = 'preparation';
      }
    } else if (event.event_type === 'time_record') {
      const subtype = event.event_subtype || '';
      if (['enter_terminal', 'exit_terminal'].includes(subtype)) {
        phase = 'execution';
      } else if (['arrive_warehouse', 'on_dock', 'empty', 'loaded', 'exit_warehouse'].includes(subtype)) {
        phase = 'execution';
      } else if (['returning', 'enter_terminal_2', 'exit_terminal_2', 'returned'].includes(subtype)) {
        phase = 'return';
      }
    }

    phases[phase].events.push(event);
    phases[phase].total++;
  });

  // Determine phase statuses based on task's current status
  const currentPhase = currentStatus ? (STATUS_TO_PHASE[currentStatus] || 'execution') : null;
  const currentPhaseIdx = currentPhase ? PHASE_ORDER.indexOf(currentPhase) : -1;

  PHASE_ORDER.forEach((phase, idx) => {
    const phaseData = phases[phase];
    if (phaseData.total === 0) return; // no events → stays 'pending'

    if (currentPhaseIdx < 0) {
      // No currentStatus provided: mark phases with events as completed (legacy behavior)
      phaseData.completed = phaseData.total;
      phaseData.status = 'completed';
    } else if (idx < currentPhaseIdx) {
      phaseData.completed = phaseData.total;
      phaseData.status = 'completed';
    } else if (idx === currentPhaseIdx) {
      // Current phase: count events as completed (they happened), but phase is in_progress
      phaseData.completed = phaseData.total;
      phaseData.status = 'in_progress';
    } else {
      // Future phases may have events from dispatcher prep (e.g., appointments set ahead)
      phaseData.completed = 0;
      phaseData.status = 'pending';
    }
  });

  return phases;
};
